import { Box } from '@spark-web/box';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { TextLink } from '@spark-web/text-link';
import { TextList } from '@spark-web/text-list';
import LoanContractItem from './LoanContractItem';

function LoanContractTermsHESL() {
  return (
    <>
      <LoanContractItem
        title="1. Your contract with us"
        terms={[
          {
            termTitle: '1.1. Contract Terms',
            message:
              'Please read these Terms and Conditions and the Schedule that includes your loan details. Together they make up your loan contract and include the information we must provide you. Your loan contract is formed by your accepting our loan offer in the manner provided for within the terms of our loan offer.',
          },
          {
            termTitle: '1.2. Ownership warranty and acknowledgment',
            message: (
              <Stack gap="xlarge">
                <Text>
                  You warrant and represent that you are the owner (or a joint owner) of the residential property at
                  which the Approved Products will be installed. Only the owner or owners of the home may be a party to
                  this loan contract.
                </Text>
                <Text>
                  You acknowledge that Approved Products funded by your Loan must not be removed from the Property until
                  the expiry or earlier termination of your loan term.
                </Text>
              </Stack>
            ),
          },
        ]}
      />
      <LoanContractItem
        title="2. Conditions of funding your loan"
        terms={[
          {
            message:
              'Before we fund your loan, you must meet our requirements including completing an authority form to nominate an account we can debit repayments from.',
          },
          {
            termTitle: '2.1. How we fund your loan',
            message: (
              <>
                <Text>Your loan will be used only for the purposes you indicated to us. This means that:</Text>
                <Box marginLeft="xlarge">
                  <TextList gap="small">
                    <Text>
                      the loan amount will be used to finance your purchase of Approved Products from a vendor under the
                      Home Energy Saver Loan by Brighte;
                    </Text>
                    <Text>the loan amount will be paid on your behalf to the vendor named in the Schedule; and</Text>
                    <Text>
                      we will pay the vendor the loan amount on your behalf, on the next business day after you have
                      acknowledged the completed installation of the Approved Products and we are informed by the vendor
                      that the purchased goods and services have been provided, delivered and/or installed (as required
                      under your contract with the vendor), or on such following business day as we reasonably agree
                      with the vendor.
                    </Text>
                  </TextList>
                </Box>
              </>
            ),
          },
        ]}
      />
      <LoanContractItem
        title="3. Managing your loan"
        terms={[
          {
            termTitle: '3.1. Transactions on your loan account',
            message: (
              <>
                <Text>
                  Once you become bound by your loan contract with us, we will open a loan account in your name. We will
                  use your loan account to debit and credit amounts owing and paid by you under your loan contract. For
                  example:
                </Text>
                <Box marginLeft="xlarge">
                  <TextList gap="small">
                    <Text>
                      when we make a payment to a vendor on your behalf, we will debit the amount of that advance to
                      your loan account; and
                    </Text>
                    <Text>
                      when we deduct from the loan amount any late payment fees that are payable to us, we will debit
                      the amount deducted to your loan account.
                    </Text>
                  </TextList>
                </Box>
              </>
            ),
          },
          {
            termTitle: '3.2. Repaying your loan',
            message: (
              <Stack gap="xlarge">
                <Text>
                  Your first repayment will become due and payable to us twenty-eight (28) days after the day on which
                  you have acknowledged the completed installation of the Approved Products funded under this loan
                  contract and we are informed by the vendor that the purchased goods and services have been provided,
                  delivered and/or installed (as required under your contract with the vendor), or on such earlier date
                  that you nominate to us as the earlier day for your first repayment, which day must fall within the
                  first 28 days after we first make any advance of the loan amount to the vendor. When you nominate an
                  earlier day for the first repayment, you must do so at least two business days before the nominated
                  date. We will tell you by email or by SMS when we have been informed by the vendor that the purchased
                  goods and services have been provided, delivered and/or installed (as required under your contract
                  with the vendor).
                </Text>
                <Text>
                  Your subsequent repayment dates will occur after your first repayment became due and payable and at
                  the periodic intervals stated in the Schedule. However:
                </Text>
                <Box marginLeft="xlarge">
                  <TextList gap="small">
                    <Text>
                      if the day for a repayment is not a business day, the required repayment is payable to us on the
                      next business day. A business day is a day on which banks are open for business in New South
                      Wales; and
                    </Text>
                    <Text>
                      if the day for a monthly repayment (where applicable) falls on a day of a month that does not
                      occur in the particular month for the applicable repayment, the repayment will be payable on the
                      last day of that month.
                    </Text>
                  </TextList>
                </Box>
                <Text>The Schedule sets out the required repayment amounts.</Text>
                <Text>
                  You must continue to make the required repayments specified in the Schedule until the outstanding
                  balance of your loan account has been paid in full. In the event that the total of the remaining
                  outstanding balance that has not yet been debited to your loan account is less than the amount of the
                  required repayment amount, then you are only required to pay the remaining unpaid outstanding balance,
                  together with any late payment fees that are then payable in order to discharge your payment
                  obligations under your loan contract.
                </Text>
                <Text>
                  Until your loan is repaid in full, together with all applicable fees, you will need to continue making
                  regular repayments, even if you’ve made extra repayments (see clause 3.3).
                </Text>
                <Text>
                  We have calculated the repayment amounts on the assumption that the whole of the loan amount is
                  advanced on the date set out in the Schedule, and on the assumptions that your first repayment will be
                  due 28 days after that date and that all repayments will be made on time and will be due and payable
                  on a business day.
                </Text>
                <Text>
                  Unless we agree otherwise, we may, acting reasonably, apply your payments to any amount you owe us and
                  in any order we choose, including towards the payment of any outstanding fees before your payment is
                  applied towards an outstanding repayment. We will give you credit for any payment on a day that is not
                  later than the day that we receive the benefit of that payment. If a repayment is dishonoured, we
                  treat it as if it was never made. Each payment that you make to us that is not made under a direct
                  debit request or other recurring payment authority must be accompanied by details of your loan with
                  us.
                </Text>
                <Text>
                  If you wish to cancel or alter your direct debit request in our favour, please call us on 1300
                  BRIGHTE. At all times during the term of this loan contract, you must ensure that we hold a valid
                  direct debit request in our favour that authorises us to collect or recover from your nominated
                  account with your financial institution all amounts that are payable to us from time to time under
                  this loan contract.
                </Text>
                <Text>
                  Subject to any statutory right of set off that you may have and that cannot be excluded by agreement,
                  you must pay all amounts you owe us without making any set-off or deducting any amounts you believe we
                  owe you.
                </Text>
              </Stack>
            ),
          },
          {
            termTitle: '3.3. Paying your loan off early',
            message: 'You may pay off your loan early without any penalty or early termination fees.',
          },
          {
            termTitle: '3.4. No further advances',
            message:
              'We have no obligation to provide any further credit or make any further advances to you under this loan contract.',
          },
          {
            termTitle: '3.5. Notices',
            message: (
              <>
                <Text>
                  Any communication, notice or other document to be given or served under or in connection with your
                  loan contract may be:
                </Text>
                <Box marginLeft="xlarge">
                  <TextList gap="small">
                    <Text>delivered personally to you;</Text>
                    <Text>posted to or left at your residential or business address last known to us;</Text>
                    <Text>sent electronically, including by email or SMS; or</Text>
                    <Text>given in any other way permitted by law.</Text>
                  </TextList>
                </Box>
                <Text>
                  The notice may be signed by any employee, solicitor, or agent on our behalf. A notice is taken to have
                  been given at the following times:
                </Text>
                <Box marginLeft="xlarge">
                  <TextList gap="small">
                    <Text>
                      notices delivered personally or left at your address will be treated as given at the time of
                      delivery;
                    </Text>
                    <Text>
                      notices sent by pre-paid post will be treated as given in the ordinary course of post, but no
                      later than on the fifth working day after posting, even if not received for any reason; and
                    </Text>
                    <Text>
                      notices sent electronically or by fax will be treated as given when the machine or system from
                      which they were sent indicates that they have been successfully transmitted or sent.
                    </Text>
                  </TextList>
                </Box>
                <Text>
                  If the law does require us to give you notice before exercising rights, we may not have to give notice
                  if:
                </Text>
                <Box marginLeft="xlarge">
                  <TextList gap="small">
                    <Text>we cannot locate you after making reasonable attempts to do so; or</Text>
                    <Text>you are insolvent; or</Text>
                    <Text>we are authorised by a court.</Text>
                  </TextList>
                </Box>
              </>
            ),
          },
        ]}
      />
      <LoanContractItem
        title="4. Fees and interest"
        terms={[
          {
            termTitle: '4.1. Fees you may have to pay',
            message:
              'The fees you need to pay are explained in the Schedule and will be debited to your loan account when they become payable.',
          },
          {
            termTitle: '4.2. Fixed interest Rate',
            message: 'No interest is payable by you under this loan contract. The annual percentage rate is 0.00%.',
          },
        ]}
      />
      <LoanContractItem
        title="5. Default and other matters"
        terms={[
          {
            termTitle: '5.1. When you’re in default',
            message:
              'You’re in default if you don’t make your required repayments on time or don’t comply with these Terms and Conditions.',
          },
          {
            termTitle: '5.2. Actions we may take',
            message: (
              <Stack gap="xlarge">
                <Text>
                  If you are in default, we may send you a notice requiring you to fix the default within the stated
                  time (at least 30 days). You may not always get a notice – for example if we have made reasonable
                  attempts to locate you without success.
                </Text>
                <Text>
                  If you are in default because of a failure to make a required payment to us, we may require you to
                  repay the whole of the outstanding balance of your loan account and any additional accrued interest
                  and fees, if you do not rectify the default within the time stated in the notice of your default.
                  Where we have elected to impose this acceleration requirement in the event of your continuing default,
                  we will tell you this in our notice to you of your default as well as the amount required to pay out
                  your loan contract (as accelerated).
                </Text>
              </Stack>
            ),
          },
          {
            termTitle: '5.3. Let us know of any changes',
            message:
              'You must tell us as soon as possible if you change your name, address, other contact details or if your situation changes.',
          },
          {
            termTitle: '5.4. Vendor fee',
            message:
              'If you have been referred to us by a vendor, then we may pay that vendor a referral fee for referring you to us. That fee, where payable, will be fixed and not dependent on your loan amount. Where that fee will be payable, the amount of the fee will be set out in the Schedule.',
          },
          {
            termTitle: '5.5. ESC Credit Charge',
            message: (
              <Stack gap="xlarge">
                <Text>Under this clause, the Participant means you and Lender means us.</Text>
                <Text>
                  By signing this contract, the Participant and the Lender acknowledge that while no interest or upfront
                  fees are payable by the Participant under this contract, a charge for the provision of credit will or
                  has been paid to the Lender by the Energy Security Corporation. The Participant acknowledges that the
                  Approved Products funded under this loan contract are being funded by the NSW Government through the
                  Home Energy Saver loan program.
                </Text>
              </Stack>
            ),
          },
          {
            termTitle: '5.6. Privacy',
            message: (
              <Text>
                Your privacy is important to us. For details of when and how we collect, use and disclose your
                information and of rights you have (for example, to choose how you want us to communicate with you), see
                our Privacy Policy at{' '}
                <TextLink href="https://brighte.com.au/privacy" target="_blank">
                  https://brighte.com.au/privacy
                </TextLink>{' '}
                or contact us on 1300-BRIGHTE.
              </Text>
            ),
          },
          {
            termTitle: '5.7. Energy Data',
            message: (
              <Stack gap="xlarge">
                <Text>
                  To help us understand your energy needs and provide you with better service, you agree that we may
                  request access to your current and historical metering data from your electricity provider on your
                  behalf. You will provide us with any additional consents or details we reasonably request to access
                  this data, including your account number, national metering identifier (NMI), distributor product
                  identifier (DPI) and meter number. Your metering data will be collected, used and disclosed in
                  accordance with our Privacy Policy.
                </Text>
                <Text>
                  You also consent to Brighte collecting your NMI/DPI data, address details and personal information
                  related to the Home Energy Saver Loan by Brighte program, and to that data being:
                </Text>
                <Box marginLeft="xlarge">
                  <TextList gap="small">
                    <Text>collected by the Approved Vendor (where applicable);</Text>
                    <Text>
                      provided to the Energy Security Corporation, the NSW Government; and the Department of Climate
                      Change, Energy, the Environment and Water
                    </Text>
                  </TextList>
                </Box>
                <Text>
                  for purposes including measurement and verification, contacting you for feedback, and evaluation of
                  the program.
                </Text>
              </Stack>
            ),
          },
          {
            termTitle: '5.8. Approved Products',
            message: (
              <Stack gap="xlarge">
                <Text>
                  Approved Products means the products eligible for funding under the Home Energy Saver loan program, as
                  specified in the program materials made available to you by us or by the Energy Security Corporation
                  from time to time.
                </Text>
                <Text>
                  You agree that you will not remove the Approved Products from your residential property prior to the
                  end of the term of this loan contract, unless the installed Approved Product has a genuine fault and
                  such fault could not be remedied via the manufacturer’s warranty or any other applicable warranty.
                </Text>
              </Stack>
            ),
          },
        ]}
      />
      <LoanContractItem title="6. Changes to your contract">
        <>
          <Text>We can:</Text>
          <Box marginLeft="xlarge">
            <TextList gap="small">
              <Text>
                change your repayments if we consider this to be necessary so that you repay your loan within the loan
                term provided for in the Schedule;
              </Text>
              <Text>change the amount of or waive any fee as long as you are no worse off by us doing so.</Text>
            </TextList>
          </Box>
          <Text>
            We will act reasonably when exercising these rights and only do so for legitimate business purposes.
          </Text>
          <Text>We will tell of these changes by giving you 30 days’ notice in writing.</Text>
        </>
      </LoanContractItem>
      <LoanContractItem title="7. Assignment">
        <Text>
          We may assign or transfer our rights under this loan contract without your consent. You cannot assign your
          rights under this loan contract without our consent.
        </Text>
      </LoanContractItem>
      <LoanContractItem title="8. Electronic consents">
        <>
          <Text>You consent to:</Text>
          <Box marginLeft="xlarge">
            <TextList gap="small">
              <Text>
                the execution of this loan contract and the loan contract offer by Brighte Capital Pty Ltd by means of
                an electronic signature and in the manner provided for in this loan contract and in the loan contract
                offer; and
              </Text>
              <Text>
                the documentation and provision to you of the loan contract (including the offer of the loan contract)
                in electronic form.
              </Text>
            </TextList>
          </Box>
          <Text>
            You acknowledge that you have already been advised by us that where your consent is given to the matters set
            out in this clause 8:
          </Text>
          <Box marginLeft="xlarge">
            <TextList gap="small">
              <Text>paper documents may no longer be given; and</Text>
              <Text>electronic communications must be regularly checked by you for electronic documents; and</Text>
              <Text>
                any consent to the giving of documents by electronic communication may be withdrawn at any time.
              </Text>
            </TextList>
          </Box>
        </>
      </LoanContractItem>
    </>
  );
}

export default LoanContractTermsHESL;
